import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import { HOLDABLE_KEYS, isFreshAccount, buildGuestPatch } from '../lib/guestProfileDraft';
// user_profiles.username is NOT NULL and UNIQUE. OAuth signups carry no
// username, so derive one the same way the handle_new_reader_signup trigger
// does, suffixed with part of the user id to keep it unique.
function fallbackUsername(user) {
  const base = (user.user_metadata?.username || user.email?.split('@')[0] || 'reader')
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 20);
  return `${base || 'reader'}_${user.id.replace(/-/g, '').slice(0, 6)}`;
}

/**
 * Hook for managing user profile data
 * Fetches profile on mount and provides update function
 */
// A guest's header edits (genres, username, location, archetype, socials,
// photo) are held here and written once they have an account, the same way
// their blocks are (ReaderProfile.jsx, migrateGuestBlocks). Before this, a
// guest's edit threw "Not authenticated": nothing stayed on screen, no sign-up
// prompt came, and Sentry logged a save failure (issue 7769097806). Which
// fields are held, and how they are written at sign-in, is in
// src/lib/guestProfileDraft.js.
export function useUserProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // State to render from, ref for the sign-in handler: that handler is set up
  // once on mount, so it would only ever see the first render's state.
  const [guestDraft, setGuestDraft] = useState(null);
  const guestDraftRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  async function fetchProfile() {
    try {
      setLoading(true);
      setError(null);

      const user = await getCurrentUser();
      if (!user) {
        setProfile(null);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError) {
        // Profile doesn't exist yet — insert one. This is a safety net only;
        // the signup trigger normally creates it. Username must be non-null,
        // so generate one the user can change later in settings.
        if (fetchError.code === 'PGRST116') {
          const { data: newProfile, error: createError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: user.id,
              username: fallbackUsername(user),
              display_name: user.user_metadata?.display_name || null,
            })
            .select()
            .single();

          if (createError) throw createError;
          setProfile(newProfile);
        } else {
          throw fetchError;
        }
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile(updates) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        // A guest's header edit stays on screen and is written at sign-in
        // (saveGuestDraft). Anything else (a block's own field) is refused as
        // before: holding it would show it as saved while nothing ever wrote
        // it. That refusal is expected for a guest, so it is not sent to Sentry.
        if (!Object.keys(updates).every(k => HOLDABLE_KEYS.has(k))) {
          return { data: null, error: 'Not signed in', notSignedIn: true };
        }
        const next = { ...(guestDraftRef.current || {}), ...updates };
        guestDraftRef.current = next;
        setGuestDraft(next);
        return { data: next, error: null, held: true };
      }

      // A guest's photo preview is a blob: address that only works on this
      // device. If the reader turned out to be signed in, never store one.
      if (typeof updates.avatar_url === 'string' && updates.avatar_url.startsWith('blob:')) {
        return { data: null, error: 'That photo has not been uploaded.', notUploaded: true };
      }

      // The profile is fetched once, on mount, so a reader who signed in on
      // this page (the sign-up prompt never leaves it) has none loaded yet.
      // Sending a generated name then replaced the one they chose (audit
      // 0.14), so read the stored one first and only generate a name for an
      // account that has no row at all.
      let username = profile?.username;
      if (!username) {
        const { data: existing, error: readError } = await supabase
          .from('user_profiles')
          .select('username')
          .eq('user_id', user.id)
          .maybeSingle();
        if (readError) throw readError;
        username = existing?.username ?? fallbackUsername(user);
      }

      // Upsert rather than update: a user whose signup trigger failed has no
      // row yet, and a plain update would match zero rows and error on .single()
      const { data, error: updateError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            user_id: user.id,
            username,
            ...updates,
          },
          { onConflict: 'user_id' }
        )
        .select()
        .single();

      if (updateError) throw updateError;

      // A guest draft still held here is one whose save at sign-in failed and
      // waits for a retry. A field just saved is newer than the draft's copy,
      // so the retry must not put the old value back.
      const held = guestDraftRef.current;
      if (held && Object.keys(updates).some(k => k in held)) {
        const rest = { ...held };
        Object.keys(updates).forEach(k => { delete rest[k]; });
        guestDraftRef.current = rest;
        setGuestDraft(rest);
      }

      setProfile(data);
      return { data, error: null };
    } catch (err) {
      console.error('Error updating profile:', err);
      // Every profile block saves through here, so this one line is how often
      // a reader's save fails gets counted at all. Until now it reached only
      // the browser console.
      Sentry.captureException(err instanceof Error ? err : new Error(`profile save: ${err?.message || String(err)}`));
      return { data: null, error: err.message, code: err.code };
    }
  }

  // Writes what a guest edited, once they have an account. Called from the
  // profile page's sign-in handler after the guest's blocks are merged, never
  // beside it: both saves send a username, and two at once could put the old
  // one back. avatarUrl is the guest's photo, uploaded by the page at sign-in;
  // the draft only ever held a preview address for it, which is never written.
  //
  // Returns { ok, error, genres }. genres is the interest_tags list written (or
  // null when none was), so the page only sends the catalogue the custom
  // genres that actually landed. On failure the draft is kept, so it stays on
  // screen and a retry can send it again.
  async function saveGuestDraft(user, { avatarUrl = null } = {}) {
    const draft = guestDraftRef.current || {};
    if (!user) return { ok: true, genres: null };
    const keep = () => { guestDraftRef.current = draft; setGuestDraft(draft); };

    const hasHeldField = Object.keys(draft).some(k => k !== 'avatar_url');
    if (!hasHeldField && !avatarUrl) {
      guestDraftRef.current = null;
      setGuestDraft(null);
      return { ok: true, genres: null };
    }

    // The whole row: if nothing ends up written, it is what the page shows,
    // since this hook only fetched once, on mount, while they were a guest.
    const { data: saved, error: readError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();
    if (readError) {
      console.error('Saving guest profile edits: reading the saved profile failed:', readError.message);
      Sentry.captureException(new Error(`guest profile edits: ${readError.message}`));
      keep();
      return { ok: false, error: readError.message, genres: null };
    }

    const patch = buildGuestPatch(draft, saved, { isNewAccount: isFreshAccount(user), avatarUrl });
    if (Object.keys(patch).length === 0) {
      guestDraftRef.current = null;
      setGuestDraft(null);
      if (saved) setProfile(saved);
      return { ok: true, genres: null };
    }

    let result = await updateProfile(patch);
    // username is UNIQUE. If the one the guest typed is taken (23505, a
    // unique violation), keep the account's own and save everything else,
    // rather than losing it all. Any other failure is reported as it is.
    if (result.error && result.code === '23505' && 'username' in patch) {
      const rest = { ...patch };
      delete rest.username;
      console.warn('Saving guest profile edits: that username is taken, keeping the account\'s own.');
      result = Object.keys(rest).length ? await updateProfile(rest) : { error: null };
    }
    if (result.error) {
      keep();
      return { ok: false, error: result.error, genres: null };
    }
    guestDraftRef.current = null;
    setGuestDraft(null);
    return { ok: true, error: null, genres: patch.interest_tags ?? null };
  }

  return {
    // A guest has no row, so they see what they edited this visit.
    profile: profile ?? guestDraft,
    loading,
    error,
    updateProfile,
    saveGuestDraft,
    refreshProfile: fetchProfile,
  };
}